import * as React from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "../lib/cn";
import { MessageBody, type MessageBodyCitationRef } from "./MessageBody";
import { MessageBubble } from "./MessageBubble";
import type { MessageCitationSourceType } from "./MessageCitation";
import { MessageMeta, type MessageMetaStatus } from "./MessageMeta";
import { MessageSystemChip } from "./MessageSystemChip";
import { MessageToolbar, type MessageToolbarAction } from "./MessageToolbar";
import { MessageTypingIndicator } from "./MessageTypingIndicator";

export type MessageKind = "user" | "agent" | "system" | "typing" | "tool";

export interface MessageProps {
  /** Discriminator — picks the visual subcomponent. */
  kind: MessageKind;
  /** Markdown body. Required for `user` / `agent` / `tool`. */
  markdown?: string;
  /** System-chip label (only when `kind === "system"`). */
  systemLabel?: React.ReactNode;
  /**
   * Author label (e.g. `"Ando"`, `"Tu"`) — rendered as a meta row ABOVE
   * the bubble. Multi-party threads (`ConnectChat`) need it: identity
   * there cannot rest on alignment alone. The main chat leaves it
   * undefined on purpose — one speaker per side, so the label was noise.
   */
  author?: React.ReactNode;
  /** Pre-formatted timestamp string. */
  timestamp?: React.ReactNode;
  /** Inline citations referenced as `[N]` in markdown. */
  citations?: MessageBodyCitationRef[];
  /** Citation pill click → opens drawer in the parent feature. */
  onCitationClick?: (sourceId: string, sourceType?: MessageCitationSourceType) => void;
  /** Status dot mode (sending / streaming / sent / error). */
  status?: MessageMetaStatus;
  /** Toolbar actions exposed on hover. Empty array hides the toolbar. */
  toolbarActions?: MessageToolbarAction[];
  /** Toolbar click handler. */
  onToolbarAction?: (action: MessageToolbarAction) => void;
  /**
   * Subset of ``toolbarActions`` rendered in the "active" state. Used
   * by ``pin`` to surface the persisted-on affordance (filled icon +
   * accent surface). Forwarded verbatim to ``MessageToolbar``.
   */
  activeToolbarActions?: ReadonlyArray<MessageToolbarAction>;
  /** Wrapper className. */
  className?: string;
  /** Children — escape hatch (system message custom content, etc.). */
  children?: React.ReactNode;
}

/**
 * Message — polymorphic wrapper around the bubble family.
 *
 * Picks the correct visual stack based on `kind`:
 *   • `user`    → right-aligned orange `MessageBubble`.
 *   • `agent`   → left-aligned transparent `MessageBubble` + body.
 *   • `system`  → standalone `MessageSystemChip` (no bubble).
 *   • `typing`  → agent-style row with `MessageTypingIndicator`.
 *   • `tool`    → agent-style bubble but flags `data-message-role="tool"`
 *                 so the consumer can style differently if needed.
 *
 * Wire-up (state, edit, retry, branch) lives in `apps/client/src/features/chat`
 * — this is the pure render layer.
 */
export const Message: React.FC<MessageProps> = ({
  kind,
  markdown,
  systemLabel,
  author,
  timestamp,
  citations,
  onCitationClick,
  status,
  toolbarActions,
  onToolbarAction,
  activeToolbarActions,
  className,
  children,
}) => {
  const [hovering, setHovering] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  // One hover/focus gesture reveals the whole row — actions AND time.
  const rowOpen = hovering || focused;
  const toolbarOpen = rowOpen && !!toolbarActions?.length;

  if (kind === "system") {
    return (
      <div className={cn("flex justify-start", className)}>
        <MessageSystemChip
          label={systemLabel ?? children}
          icon={status === "error" ? <AlertCircle size={14} strokeWidth={2} /> : undefined}
          role={status === "error" ? "alert" : undefined}
          className={status === "error" ? "rounded-xl py-2 text-ink-1" : undefined}
        />
      </div>
    );
  }

  if (kind === "typing") {
    return (
      <MessageBubble
        variant="agent"
        showAvatar
        className={className}
        header={
          author ? <MessageMeta author={author} status="streaming" /> : null
        }
      >
        <MessageTypingIndicator />
      </MessageBubble>
    );
  }

  if (kind === "user" && !markdown?.trim() && !children) return null;

  const isUser = kind === "user";
  const dataKind = kind === "tool" ? "tool" : isUser ? "user" : "agent";

  return (
    <div
      data-message-kind={dataKind}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setFocused(false);
        }
      }}
      className={cn("relative", isUser ? "flex flex-col items-end" : "block")}
    >
      <MessageBubble
        variant={isUser ? "user" : "agent"}
        showAvatar={!isUser}
        className={className}
        header={
          author ? (
            <MessageMeta author={author} status={status} align={isUser ? "end" : "start"} />
          ) : null
        }
      >
        {markdown ? (
          <MessageBody
            markdown={markdown}
            citations={citations}
            onCitationClick={onCitationClick}
            className={isUser ? "[&_*]:text-white" : undefined}
            streaming={!isUser && status === "streaming"}
          />
        ) : (
          children
        )}
      </MessageBubble>
      {timestamp || toolbarActions?.length ? (
        // Reserve footer space so actions never overlap attachments or disclosures.
        <div
          className={cn(
            "flex min-h-7 flex-wrap items-start gap-x-2 gap-y-1 pt-0.5 transition-opacity duration-fast",
            isUser ? "flex-row-reverse" : "flex-row",
            // Hidden actions keep their space and do not intercept pointer input.
            rowOpen ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        >
          {toolbarActions?.length ? (
            <MessageToolbar
              open={toolbarOpen}
              actions={toolbarActions}
              activeActions={activeToolbarActions}
              onAction={onToolbarAction}
            />
          ) : null}
          {timestamp ? (
            // No brand dot next to the time: it read as decoration. The
            // dot survives ONLY for `error`, the single signal that a
            // send failed.
            <MessageMeta
              timestamp={timestamp}
              status={status === "error" ? "error" : undefined}
              className="min-w-0 text-ink-3 normal-case tracking-normal"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
Message.displayName = "Message";
